<!-- Global search (#searchModal, z-65): a text field and up to 25 results (icon, title, type and the start of the text);
     "@hook", "@video", "@fonte"… filter by type. A click on a result closes the search and opens the item in its page.
     Opened with Ctrl/Cmd+K (src/features/keyboard.ts), the menu's Search and the Home tile through the dialogs store
     . Closes with a click outside or Esc (src/features/keyboard.ts, step "Search"). Logic in
     src/features/search.ts. -->
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from '../../composables/useI18n'
import { useProfileData } from '../../composables/useProfileData'
import { useDialogsStore } from '../../stores/dialogs'
import { useCreatorsStore } from '../../stores/creators'
import { SEARCH_ICONS, SEARCH_LABELS, SEARCH_TEXT_MAX, search, searchItems, type SearchResult } from '../../features/search'
import { useDialogEntry } from '../../composables/useDialogEntry'
import { richPlain } from '../../features/richText'
import { openVideo } from '../../stores/video'
import { app } from '../../features/app'
import { useEditorStore } from '../../stores/editor'

const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()
const dialogs = useDialogsStore()
const creators = useCreatorsStore()

const query = ref('')
const data = useProfileData()
// The index is rebuilt only when the data changes (a save or a refresh), not on every key
const items = computed(() => searchItems(data.value, text => richPlain(text, data.value)))
const results = computed(() => search(items.value, query.value))

function pick(r: SearchResult) {
  emit('close')
  if (r.kind === 'hook') { app.switchTab('vault'); dialogs.show('hook', { id: r.id }) }
  else if (r.kind === 'video') { app.switchTab('ped'); openVideo(r.id) }
  else if (r.kind === 'creator') { app.switchTab('creators'); creators.openDetail(r.id) }
  else if (r.kind === 'av') { app.switchTab('creators'); creators.openDetail(r.creatorId!); creators.openAnalysed(r.id) }
  else if (r.kind === 'source') { app.switchTab('sources'); dialogs.show('source', { id: r.id }) }
  else if (r.kind === 'saved') { app.switchTab('saved'); dialogs.show('saved', { id: r.id }) }
  else useEditorStore().openEditor(r.id)
}

const input = ref<HTMLInputElement | null>(null)
// In the dialog stack (Esc is handled by its own step in src/features/keyboard.ts); a click outside closes it
const { onDown, onClick } = useDialogEntry({ name: 'search', esc: false, editing: false }, () => emit('close'))
onMounted(() => input.value?.focus())
</script>

<template>
  <Teleport to="body">
    <div id="searchModal" class="fixed inset-0 bg-black/70 backdrop-blur-sm z-[65] flex items-start justify-center p-4 pt-[10vh]" @mousedown="onDown" @click="onClick">
      <div class="bg-darkcard border border-gray-800 w-full max-w-xl lg:max-w-2xl xl:max-w-3xl 2xl:max-w-4xl rounded-2xl shadow-2xl overflow-hidden">
        <input id="searchInput" ref="input" :value="query" :placeholder="t('Search... (@hook, @video, @creator, @source to filter)')"
          class="w-full bg-transparent px-5 py-4 text-gray-100 focus:outline-none border-b border-gray-800" @input="query = ($event.target as HTMLInputElement).value">
        <div id="searchResults" class="max-h-[50vh] overflow-y-auto p-2">
          <div v-for="(r, i) in results" :key="i" class="flex gap-3 p-3 rounded-lg hover:bg-darkbtn cursor-pointer" @click="pick(r)"><i :class="['fas', SEARCH_ICONS[r.kind], 'text-brand mt-1 w-4']"></i><div class="min-w-0"><div class="text-sm font-semibold truncate">{{ r.title }}</div><div class="text-[11px] text-gray-400 truncate"><span class="text-brand">{{ t(SEARCH_LABELS[r.kind]) }}</span> {{ (r.text || '').slice(0, SEARCH_TEXT_MAX) }}</div></div></div>
          <div v-if="!results.length" class="p-4 text-xs text-gray-500">{{ t('No results') }}</div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
